import React, { useMemo, useState } from "react";
import { employees, rosters, shifts } from "../data/sampleData";
import { detectBackToBack, getWorkedShifts } from "../../utils/calculateFatigue";
import "./SiteRosterMatrix.css";

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  Legend
} from "recharts";

export default function SiteRosterDashboar({
  days,
  siteEmployees,
  rosterMap,
  site,
  getWorkedShifts,
  detectBackToBack,
  onFilter
}) {

  /* ===========================
     AGGREGATION (SINGLE PASS)
  ========================== */
  const data = useMemo(() => {
    const byShift = {};
    const byDate = {};
    const byEmployee = {};
    const deviations = {
      MISSED: 0,
      UNPLANNED: 0,
      ABSENT: 0,
      FATIGUE: 0
    };

    Object.keys(site.minRequiredPerShift).forEach(s => {
      byShift[s] = { required: 0, worked: 0 };
    });

    days.forEach(date => {
      byDate[date] = { planned: 0, worked: 0 };

      siteEmployees.forEach(emp => {
        byEmployee[emp.id] ||= {
          name: emp.name,
          workedDays: 0,
          fatigueDays: 0
        };

        const planned =
          rosterMap[date]?.[emp.id]
            ?.filter(s => s.unitId === site.siteId)
            .map(s => s.shift) || [];

        const worked = getWorkedShifts(emp.id, date);

        planned.forEach(s => {
          byShift[s].required++;
          byDate[date].planned++;
        });

        if (worked.length) {
          byEmployee[emp.id].workedDays++;
          byDate[date].worked++;
        }

        worked.forEach(w => {
          if (byShift[w.shift]) byShift[w.shift].worked++;
          if (!planned.includes(w.shift)) deviations.UNPLANNED++;
        });

        const missed = planned.filter(
          s => !worked.some(w => w.shift === s && w.unitId === site.siteId)
        );

        deviations.MISSED += missed.length;
        if (planned.length && worked.length === 0) deviations.ABSENT++;

        if (detectBackToBack(emp.id, date)) {
          deviations.FATIGUE++;
          byEmployee[emp.id].fatigueDays++;
        }
      });
    });

    return { byShift, byDate, byEmployee, deviations };
  }, [days, siteEmployees, rosterMap, site, getWorkedShifts, detectBackToBack]);

  /* ===========================
     CHART DATA SHAPING
  ========================== */
  const shiftBar = Object.entries(data.byShift).map(([k, v]) => ({
    shift: k,
    Required: v.required,
    Worked: v.worked,
    Gap: v.worked - v.required
  }));

  const trend = Object.entries(data.byDate).map(([d, v]) => ({
    day: d.slice(8, 10),
    Planned: v.planned,
    Worked: v.worked
  }));

  const pie = Object.entries(data.deviations).map(([k, v]) => ({
    name: k,
    value: v
  }));

  const fatigue = Object.values(data.byEmployee)
    .filter(e => e.fatigueDays > 0)
    .map(e => ({ name: e.name, days: e.fatigueDays }));

  /* ===========================
     RENDER
  ========================== */
  return (
    <div className="dashboard-grid">

      {/* SHIFT COVERAGE */}
      <div className="chart-card">
        <h4>Shift Coverage</h4>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={shiftBar}>
            <XAxis dataKey="shift" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="Required" fill="#e5e7eb" />
            <Bar
              dataKey="Worked"
              fill="#22c55e"
              onClick={(d) => onFilter(d.shift)}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* STAFFING TREND */}
      <div className="chart-card">
        <h4>Planned vs Worked</h4>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={trend}>
            <XAxis dataKey="day" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line dataKey="Planned" stroke="#6366f1" />
            <Line dataKey="Worked" stroke="#22c55e" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* DEVIATION PIE */}
      <div className="chart-card">
        <h4>Deviation Breakdown</h4>
        <ResponsiveContainer width="100%" height={260}>
          <PieChart>
            <Pie
              data={pie}
              dataKey="value"
              outerRadius={90}
              onClick={(d) => onFilter(d.name)}
            >
              {pie.map((_, i) => (
                <Cell
                  key={i}
                  fill={["#ef4444", "#3b82f6", "#f59e0b", "#9ca3af"][i]}
                />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* FATIGUE */}
      <div className="chart-card wide">
        <h4>Fatigue Risk (Days)</h4>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={fatigue} layout="vertical">
            <XAxis type="number" />
            <YAxis type="category" dataKey="name" width={120} />
            <Tooltip />
            <Bar dataKey="days" fill="#f59e0b" />
          </BarChart>
        </ResponsiveContainer>
      </div>

    </div>
  );
}
